import React, { useCallback, useEffect, useState } from "react";
import { Button, Chip, CircularProgress, IconButton, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import Icon from "@mdi/react";
import { mdiRefresh, mdiUndo, mdiAlertCircle, mdiAlertOutline, mdiCheckCircle, mdiCodeJson } from "@mdi/js";
import {
	BackofficeAdminServices,
	NotistackServices,
	CompensationLogFilters,
	CompensationLogResponse,
	PublicCompensationLogDTO,
	CompensationStatus,
} from "shared/services";
import { HandleError } from "shared/utils";
import styles from "./styles.module.scss";

const PAGE_SIZE = 50;

const STATUS_META: Record<CompensationStatus, { label: string; color: any; icon: string }> = {
	rolled_back: { label: "Rolled back", color: "success", icon: mdiCheckCircle },
	partial: { label: "Parcial", color: "warning", icon: mdiAlertOutline },
	rollback_failed: { label: "Falhou", color: "error", icon: mdiAlertCircle },
};

function fmtDate(iso: string): string {
	return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function fmtDuration(ms: number): string {
	if (ms < 1000) return `${ms}ms`;
	return `${(ms / 1000).toFixed(2)}s`;
}

export const CompensationsPage: React.FC = () => {
	const [data, setData] = useState<CompensationLogResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [expanded, setExpanded] = useState<string | null>(null);
	const [operationName, setOperationName] = useState("");
	const [accountId, setAccountId] = useState("");
	const [statusFilter, setStatusFilter] = useState<"all" | CompensationStatus>("all");
	const [offset, setOffset] = useState(0);

	const fetchData = useCallback(async () => {
		setLoading(true);
		const filters: CompensationLogFilters = { limit: PAGE_SIZE, offset };
		if (operationName.trim()) filters.operationName = operationName.trim();
		if (accountId.trim()) filters.accountId = accountId.trim();
		if (statusFilter !== "all") filters.status = statusFilter;
		try {
			const response = await BackofficeAdminServices.listCompensationLog(filters);
			setData(response);
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar compensation log");
		} finally {
			setLoading(false);
		}
	}, [operationName, accountId, statusFilter, offset]);

	useEffect(() => {
		void fetchData();
	}, [fetchData]);

	const handleApplyFilters = () => {
		setOffset(0);
		void fetchData();
	};

	const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
	const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

	return (
		<div className={styles.page}>
			<div className={styles.header}>
				<div>
					<h1 className={styles.title}>
						<Icon path={mdiUndo} size={1} /> Compensation Log
					</h1>
					<p className={styles.subtitle}>
						EK-281 · Saga-style rollbacks executados em operações multi-step que falharam parcialmente.
					</p>
				</div>
				<div className={styles.actions}>
					<Button
						size="small"
						variant="outlined"
						onClick={() => void fetchData()}
						disabled={loading}
						startIcon={<Icon path={mdiRefresh} size={0.8} />}
					>
						Recarregar
					</Button>
				</div>
			</div>

			<div className={styles.filters}>
				<TextField
					size="small"
					placeholder="Operation name (hire-member, ...)"
					value={operationName}
					onChange={(e) => setOperationName(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
				/>
				<TextField
					size="small"
					placeholder="Account ID"
					value={accountId}
					onChange={(e) => setAccountId(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
				/>
				<Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
					<MenuItem value="all">Todos</MenuItem>
					<MenuItem value="rolled_back">Rolled back</MenuItem>
					<MenuItem value="partial">Parcial</MenuItem>
					<MenuItem value="rollback_failed">Falhou</MenuItem>
				</Select>
				<Button variant="contained" size="small" onClick={handleApplyFilters}>
					Filtrar
				</Button>
			</div>

			{loading && !data ? (
				<div className={styles.loadingState}>
					<CircularProgress size={32} />
				</div>
			) : !data || data.items.length === 0 ? (
				<div className={styles.empty}>
					<p>🎉 Nenhuma compensation no log (com os filtros aplicados).</p>
				</div>
			) : (
				<>
					<div className={styles.summary}>
						<span>
							<strong>{data.total}</strong> total · página <strong>{currentPage}/{totalPages}</strong>
						</span>
						<div className={styles.pager}>
							<Button size="small" variant="outlined" onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))} disabled={offset === 0 || loading}>
								‹ Anterior
							</Button>
							<Button size="small" variant="outlined" onClick={() => setOffset((o) => o + PAGE_SIZE)} disabled={!data || offset + PAGE_SIZE >= data.total || loading}>
								Próxima ›
							</Button>
						</div>
					</div>

					<div className={styles.tableWrapper}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th>Quando</th>
									<th>Status</th>
									<th>Operação</th>
									<th>Compensations</th>
									<th>Duration</th>
									<th>Trigger error</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{data.items.map((row) => (
									<RowItem
										key={row.id}
										row={row}
										expanded={expanded === row.id}
										onToggle={() => setExpanded((e) => (e === row.id ? null : row.id))}
									/>
								))}
							</tbody>
						</table>
					</div>
				</>
			)}
		</div>
	);
};

const RowItem: React.FC<{ row: PublicCompensationLogDTO; expanded: boolean; onToggle: () => void }> = ({ row, expanded, onToggle }) => {
	const meta = STATUS_META[row.status];
	return (
		<>
			<tr className={styles.row} onClick={onToggle}>
				<td className={styles.colDate}>{fmtDate(row.createdAt)}</td>
				<td>
					<Chip size="small" icon={<Icon path={meta.icon} size={0.7} />} label={meta.label} color={meta.color} variant={row.status === "rolled_back" ? "outlined" : "filled"} />
				</td>
				<td className={styles.mono}>{row.operationName}</td>
				<td className={styles.mono}>
					{row.compensationsOk}/{row.compensationsTotal}
					{row.compensationsFailed > 0 && <span className={styles.failedBadge}> · {row.compensationsFailed} falhas</span>}
				</td>
				<td className={styles.mono}>{fmtDuration(row.durationMs)}</td>
				<td className={styles.reason}>
					<Tooltip title={row.triggerErrorMessage} placement="top-start">
						<span>{row.triggerErrorMessage.slice(0, 80)}{row.triggerErrorMessage.length > 80 ? "…" : ""}</span>
					</Tooltip>
				</td>
				<td>
					<IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
						<Icon path={mdiCodeJson} size={0.7} />
					</IconButton>
				</td>
			</tr>
			{expanded && (
				<tr className={styles.expandedRow}>
					<td colSpan={7}>
						<div className={styles.expandedContent}>
							<div className={styles.errorBox}>
								<strong>Trigger error:</strong> {row.triggerErrorMessage}
							</div>
							<div className={styles.previewSection}>
								<h4>Compensations executadas (LIFO)</h4>
								<table className={styles.subTable}>
									<thead>
										<tr><th>#</th><th>Nome</th><th>Status</th><th>Duration</th><th>Erro</th></tr>
									</thead>
									<tbody>
										{row.compensations.map((c, idx) => (
											<tr key={idx}>
												<td className={styles.idx}>{idx + 1}</td>
												<td className={styles.mono}>{c.name}</td>
												<td>{c.success ? <Chip size="small" label="OK" color="success" variant="outlined" /> : <Chip size="small" label="FAIL" color="error" />}</td>
												<td className={styles.mono}>{fmtDuration(c.durationMs)}</td>
												<td className={styles.reason}>{c.error ?? "—"}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							{row.triggerErrorStack && (
								<div className={styles.previewSection}>
									<h4>Stack trace</h4>
									<pre className={styles.json}>{row.triggerErrorStack}</pre>
								</div>
							)}
							{row.metadata && (
								<div className={styles.previewSection}>
									<h4>Metadata</h4>
									<pre className={styles.json}>{JSON.stringify(row.metadata, null, 2)}</pre>
								</div>
							)}
						</div>
					</td>
				</tr>
			)}
		</>
	);
};
